
import React, { useState, useEffect } from 'react';
import { SubjectType, CurriculumNode, Language, DailyTask, Theme } from './types';
import { Academy } from './features/Academy';
import { LessonView } from './features/LessonView';
import { AuthView } from './features/AuthView';
import { OnboardingWizard } from './features/OnboardingWizard';
import { useWonderEngine } from './hooks/useWonderEngine';
import { translations } from './i18n';
import { Loader2 } from 'lucide-react';

import { GlobalLayout } from './components/layout/GlobalLayout';
import { HomePage } from './pages/HomePage';

const App: React.FC = () => {
  const [lang, setLang] = useState<Language>(() => 
    typeof navigator !== 'undefined' && navigator.language.startsWith('zh') ? 'zh' : 'en'
  );
  const [theme, setTheme] = useState<Theme>('light');
  
  const [view, setView] = useState<'home' | 'academy' | 'lesson'>('home');
  const [activeSubject, setActiveSubject] = useState<SubjectType>('math');
  const [activeNode, setActiveNode] = useState<CurriculumNode | null>(null);
  const [dailyTasks, setDailyTasks] = useState<DailyTask[]>([]);
  
  const { session, user, curriculum, loading, actions } = useWonderEngine(lang);
  const t = translations[lang];

  useEffect(() => {
    if (theme === 'dark') document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');
  }, [theme]);

  const handleStartLesson = (node: CurriculumNode) => {
    const success = actions.startLesson(node);
    if (success) {
      setActiveNode(node);
      setView('lesson');
    } else {
      alert(t.lowEnergy); 
    }
  };

  const handleFreeExplore = (topic: string) => {
    const tempNode: CurriculumNode = {
      id: `free_${Date.now()}`,
      title: topic,
      desc: t.warpDesc,
      level: user?.level || 1,
      prev: null
    };
    handleStartLesson(tempNode);
  };

  const handleCompleteLesson = async (actualSubject: SubjectType) => {
    if (!activeNode || !user) return;
    
    // Log the lesson with the correct AI-determined subject
    await actions.completeLesson(activeNode, actualSubject);
    
    setView('home');
    setActiveNode(null);
  };

  const addDailyTask = (subject: SubjectType) => {
    setDailyTasks([...dailyTasks, {
      id: Date.now().toString(),
      subject,
      desc: `${t.subjects[subject]}`,
      completed: false,
      createdAt: Date.now()
    }]);
  };

  const removeTask = (id: string) => setDailyTasks(p => p.filter(t => t.id !== id));
  const toggleTask = (id: string) => setDailyTasks(p => p.map(t => t.id === id ? { ...t, completed: !t.completed } : t));

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center text-white">
        <Loader2 size={48} className="animate-spin text-indigo-400 mb-6" />
        <h2 className="text-xl font-orbitron tracking-[0.2em] animate-pulse uppercase text-indigo-200">System Sync...</h2>
      </div>
    );
  }

  if (!session) {
    return <AuthView lang={lang} onLangToggle={() => setLang(l => l === 'en' ? 'zh' : 'en')} />;
  }

  if (user && user.interests.length === 0) {
    return <OnboardingWizard user={user} lang={lang} onComplete={() => window.location.reload()} />;
  }

  if (!user || !curriculum) return null;

  return (
    <>
      {view === 'lesson' && activeNode && (
        <LessonView 
          node={activeNode}
          subject={activeSubject}
          user={user}
          lang={lang}
          theme={theme}
          onComplete={handleCompleteLesson}
          onExit={() => setView('home')}
        />
      )}

      {view !== 'lesson' && (
        <GlobalLayout
          user={user}
          lang={lang}
          theme={theme}
          onToggleLang={() => setLang(l => l === 'en' ? 'zh' : 'en')}
          onToggleTheme={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
          onHome={() => setView('home')}
          dailyTasks={dailyTasks}
          onAddTask={addDailyTask}
          onRemoveTask={removeTask}
          onToggleTask={toggleTask}
          onEnergyCharge={actions.chargeEnergy}
        >
          {view === 'home' && (
            <HomePage 
              user={user}
              lang={lang}
              onSubjectSelect={(s) => { setActiveSubject(s); setView('academy'); }}
              onStartAdventure={handleFreeExplore}
              onContinue={() => setView('academy')}
            />
          )}

          {view === 'academy' && (
            <Academy 
              curriculum={curriculum}
              unlockedSkills={user.skills}
              activeSubject={activeSubject}
              lang={lang}
              onSubjectChange={setActiveSubject}
              onNodeSelect={handleStartLesson}
              onUpdateCurriculum={actions.updateCurriculum}
              user={user}
            />
          )}
        </GlobalLayout>
      )}
    </>
  );
};

export default App;
